import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input, Button, useToast } from "../design-system";

const BLOCKED_PASSWORDS = [
  "password", "password1", "password12", "password123", "password1234",
  "qwerty", "qwerty123", "letmein", "welcome", "welcome1",
  "abc123", "abcdef", "123456", "1234567", "12345678", "123456789",
  "admin", "admin123", "administrator", "login", "master",
  "monkey", "dragon", "football", "baseball", "soccer",
  "iloveyou", "trustno1", "sunshine", "princess", "shadow",
  "superman", "michael", "charlie", "donald", "access",
  "hello", "passw0rd", "p@ssword", "p@ssw0rd",
  "changeme", "test", "test123", "guest", "default",
  "inspection", "inspector", "inspector1", "inspector123",
  "butler", "butler123", "septic", "septic123",
  "spectora", "homegauge", "tanktrak"
];

function getPasswordRules(password) {
  return [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "One uppercase letter (A-Z)", met: /[A-Z]/.test(password) },
    { label: "One lowercase letter (a-z)", met: /[a-z]/.test(password) },
    { label: "One number (0-9)", met: /[0-9]/.test(password) },
    { label: "One special character", met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password) },
    { label: "Not a commonly used password", met: password.length > 0 && !BLOCKED_PASSWORDS.includes(password.toLowerCase()) }
  ];
}

function PasswordStrength({ password }) {
  const rules = getPasswordRules(password);
  const metCount = rules.filter(r => r.met).length;
  const allMet = metCount === rules.length;
  const started = password.length > 0;
  if (!started) return null;
  const pct = Math.round((metCount / rules.length) * 100);
  const barColor = pct < 50 ? "var(--color-error)" : pct < 100 ? "var(--color-warning)" : "var(--color-success)";
  const label = pct < 50 ? "Weak" : pct < 100 ? "Getting there" : "Strong";

  return (
    <div style={{
      marginTop: "var(--space-2)", padding: "var(--space-3) var(--space-4)",
      backgroundColor: allMet ? "var(--color-success-50)" : "var(--surface-sunken)",
      borderRadius: "var(--radius-lg)",
      border: allMet ? "1px solid var(--color-success-200)" : "1px solid var(--border-default)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
        <div style={{ flex: 1, height: 4, backgroundColor: "var(--border-default)", borderRadius: "var(--radius-full)" }}>
          <div style={{ width: `${pct}%`, height: "100%", backgroundColor: barColor, borderRadius: "var(--radius-full)", transition: "width 0.3s" }} />
        </div>
        <span style={{ fontSize: "var(--text-caption)", fontWeight: "var(--weight-semibold)", color: barColor }}>{label}</span>
      </div>
      {rules.map((rule, i) => (
        <div key={i} style={{
          fontSize: "var(--text-caption)", color: rule.met ? "var(--color-success-600)" : "var(--text-tertiary)",
          display: "flex", alignItems: "center", gap: "var(--space-1_5)", marginBottom: "var(--space-0_5)",
          fontFamily: "var(--font-family)",
        }}>
          <span style={{ fontSize: "var(--text-body-sm)", width: "16px", textAlign: "center" }}>{rule.met ? "\u2713" : "\u2022"}</span>
          <span style={{ textDecoration: rule.met ? "line-through" : "none" }}>{rule.label}</span>
        </div>
      ))}
    </div>
  );
}

function RegisterPage() {
  const auth = useAuth();
  const toast = useToast();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", companyName: "", inviteCode: "" });
  const [mode, setMode] = useState("create"); // "create" | "join"
  const switchMode = (m) => { setMode(m); setForm((prev) => ({ ...prev, companyName: "", inviteCode: "" })); };
  const [loading, setLoading] = useState(false);
  // When email verification is enforced, registration doesn't log you in — it
  // sends a link. We flip to a "check your email" screen instead of navigating.
  const [verifySent, setVerifySent] = useState(null); // holds the email address
  const [resending, setResending] = useState(false);

  if (auth.isAuthenticated) { if (typeof window !== 'undefined') window.location.href = '/app/'; return null; }

  const passwordValid = getPasswordRules(form.password).every(r => r.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "create" && !form.companyName.trim()) { toast.error("Company name is required"); return; }
    if (mode === "join" && !form.inviteCode.trim()) { toast.error("Invite code is required"); return; }
    if (!form.firstName.trim()) { toast.error("First name is required"); return; }
    if (!form.lastName.trim()) { toast.error("Last name is required"); return; }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(form.email)) { toast.error("Please enter a valid email address"); return; }
    if (!passwordValid) { toast.error("Please fix the password requirements below."); return; }

    setLoading(true);
    try {
      const result = await auth.register(form);
      if (result && result.emailVerificationRequired) {
        setVerifySent(result.email || form.email);
        toast.success("Account created — check your email to verify.");
      } else {
        toast.success("Account created! Welcome aboard.");
      }
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await auth.resendVerification(verifySent);
      toast.success("Verification email sent again.");
    } catch (err) {
      toast.error(err.message || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // "Check your email" confirmation screen (shown after a verification-required signup).
  if (verifySent) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "var(--space-6) var(--space-4)", backgroundColor: "var(--surface-page)" }}>
        <div style={{ width: "100%", maxWidth: "440px", textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "var(--space-4)" }}>{"✉️"}</div>
          <h1 style={{ fontSize: "var(--text-h2)", fontWeight: "var(--weight-bold)", color: "var(--text-primary)", margin: "0 0 var(--space-3)", fontFamily: "var(--font-family)", letterSpacing: "var(--tracking-tight)" }}>Check your email</h1>
          <p style={{ fontSize: "var(--text-body-md)", color: "var(--text-secondary)", margin: "0 0 var(--space-2)", fontFamily: "var(--font-family)" }}>
            We sent a verification link to <strong style={{ color: "var(--text-primary)" }}>{verifySent}</strong>.
          </p>
          <p style={{ fontSize: "var(--text-body-sm)", color: "var(--text-tertiary)", margin: "0 0 var(--space-6)", fontFamily: "var(--font-family)" }}>
            Click the link in that email to finish creating your account. It expires in 24 hours. Until then, you can't sign in.
          </p>
          <Button variant="secondary" size="lg" fullWidth loading={resending} onClick={handleResend}>Resend verification email</Button>
          <p style={{ textAlign: "center", marginTop: "var(--space-6)", fontSize: "var(--text-body-sm)", color: "var(--text-secondary)", fontFamily: "var(--font-family)" }}>
            <Link to="/login" style={{ color: "var(--color-primary)", fontWeight: "var(--weight-semibold)", textDecoration: "none" }}>Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "var(--space-6) var(--space-4)", backgroundColor: "var(--surface-page)" }}>
      <div style={{ width: "100%", maxWidth: "440px" }}>
        <div style={{ textAlign: "center", marginBottom: "var(--space-7)" }}>
          <h1 style={{ fontSize: "var(--text-h2)", fontWeight: "var(--weight-bold)", color: "var(--text-primary)", margin: "0 0 var(--space-1_5)", fontFamily: "var(--font-family)", letterSpacing: "var(--tracking-tight)" }}>Get started</h1>
          <p style={{ fontSize: "var(--text-body-md)", color: "var(--text-secondary)", margin: 0, fontFamily: "var(--font-family)" }}>Create your Inspection Butler account</p>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-4)" }}>
            <button type="button" onClick={() => switchMode("create")} style={{
              flex: 1, padding: "var(--space-2_5) var(--space-3)", borderRadius: "var(--radius-lg)",
              border: mode === "create" ? "2px solid var(--color-primary)" : "1px solid var(--border-default)",
              backgroundColor: mode === "create" ? "var(--surface-sunken)" : "var(--surface-page)",
              fontWeight: "var(--weight-semibold)", fontSize: "var(--text-body-sm)", color: "var(--text-primary)",
              cursor: "pointer", fontFamily: "var(--font-family)",
            }}>Create a new company</button>
            <button type="button" onClick={() => switchMode("join")} style={{
              flex: 1, padding: "var(--space-2_5) var(--space-3)", borderRadius: "var(--radius-lg)",
              border: mode === "join" ? "2px solid var(--color-primary)" : "1px solid var(--border-default)",
              backgroundColor: mode === "join" ? "var(--surface-sunken)" : "var(--surface-page)",
              fontWeight: "var(--weight-semibold)", fontSize: "var(--text-body-sm)", color: "var(--text-primary)",
              cursor: "pointer", fontFamily: "var(--font-family)",
            }}>I have an invite code</button>
          </div>
          {mode === "create" ? (
            <div style={{ marginBottom: "var(--space-4)" }}>
              <Input label="Company Name" size="lg" value={form.companyName} onChange={update("companyName")} placeholder="Your inspection company" autoCapitalize="words" autoCorrect="off" />
            </div>
          ) : (
            <div style={{ marginBottom: "var(--space-4)" }}>
              <Input label="Invite Code" size="lg" value={form.inviteCode} onChange={update("inviteCode")} placeholder="e.g. Q4HCWADJ" autoCapitalize="characters" autoCorrect="off" />
              <p style={{ fontSize: "var(--text-caption)", color: "var(--text-tertiary)", margin: "var(--space-1) 0 0", fontFamily: "var(--font-family)" }}>
                Ask your company's admin for this code.
              </p>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)", marginBottom: "var(--space-4)" }}>
            <Input label="First Name" size="lg" value={form.firstName} onChange={update("firstName")} placeholder="James" autoCapitalize="words" autoCorrect="off" />
            <Input label="Last Name" size="lg" value={form.lastName} onChange={update("lastName")} placeholder="Butler" autoCapitalize="words" autoCorrect="off" />
          </div>
          <div style={{ marginBottom: "var(--space-4)" }}>
            <Input label="Email" type="email" size="lg" value={form.email} onChange={update("email")} placeholder="you@company.com" autoCapitalize="none" autoCorrect="off" autoComplete="email" />
          </div>
          <div style={{ marginBottom: "var(--space-6)" }}>
            <Input label="Password" type="password" size="lg" value={form.password} onChange={update("password")} placeholder="Create a strong password" autoComplete="new-password" />
            <PasswordStrength password={form.password} />
          </div>
          <Button type="submit" variant="primary" size="xl" fullWidth loading={loading}>Create Account</Button>
        </form>
        <p style={{ textAlign: "center", marginTop: "var(--space-6)", fontSize: "var(--text-body-sm)", color: "var(--text-secondary)", fontFamily: "var(--font-family)" }}>
          Already have an account?{" "}
          <Link to="/login" style={{ color: "var(--color-primary)", fontWeight: "var(--weight-semibold)", textDecoration: "none" }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterPage;
